/**
 * Transform API payment response to frontend data structure
 * Combines deterministic and probabilistic track data into unified format
 */

/**
 * Transform API response from /api/user/payment/list to frontend PaymentsReport structure
 * @param {Object} deterministicData - API response for deterministic track
 * @param {Object} probabilisticData - API response for probabilistic track
 * @returns {Object} Transformed data for PaymentsReport component
 */
export const transformPaymentData = (deterministicData, probabilisticData) => {
  // Use whichever track data is available for common fields
  const baseData = deterministicData || probabilisticData;

  if (!baseData) {
    return null;
  }

  // Calculate participation rate
  const participationRate = baseData.nr_expected_scores > 0
    ? ((baseData.nr_scores / baseData.nr_expected_scores) * 100).toFixed(1)
    : 0;

  // Build daily details by merging both tracks
  const dailyDetails = buildDailyDetails(
    deterministicData?.daily_payments_json,
    probabilisticData?.daily_payments_json
  );

  // Calculate total payment (sum of both tracks)
  const detTotal = deterministicData?.total_payment || 0;
  const probTotal = probabilisticData?.total_payment || 0;

  // Calculate outstanding and month payments (sum of both tracks)
  const detOutstanding = deterministicData?.outstanding_payment || 0;
  const probOutstanding = probabilisticData?.outstanding_payment || 0;
  const detMonth = deterministicData?.month_payment || 0;
  const probMonth = probabilisticData?.month_payment || 0;

  return {
    forecaster: {
      id: baseData.user,
      email: baseData.email,
    },
    period: {
      year: baseData.year,
      month: baseData.month,
      daysInMonth: baseData.nr_expected_scores,
    },
    eligibility: {
      daysSubmitted: baseData.nr_scores,
      daysMissed: baseData.nr_days_missed,
      participationRate: parseFloat(participationRate),
      isEligibleForDailyRewards: baseData.nr_scores > 0,
      isEligibleForLeagueRewards: baseData.nr_days_missed <= 5,
    },
    deterministic: deterministicData ? buildTrackData(deterministicData) : null,
    probabilistic: probabilisticData ? buildTrackData(probabilisticData) : null,
    totalPayment: detTotal + probTotal,
    monthPayment: detMonth + probMonth,
    outstandingPayment: detOutstanding + probOutstanding,
    dailyDetails,
  };
};

/**
 * Build track-specific data structure
 * @param {Object} trackData - API response for a single track
 * @returns {Object} Track data formatted for frontend
 */
const buildTrackData = (trackData) => {
  if (!trackData) return null;

  return {
    ranking: {
      monthlyRank: trackData.avg_score_rank,
      league: trackData.league,
      totalForecasters: trackData.nr_forecasters,
    },
    scores: {
      avgScoreWithPenalties: trackData.avg_score,
    },
    payments: {
      leagueBonus: trackData.league_payment || 0,
      peakErrorBonus: trackData.league_bonus_payment || 0,
      dailyRewardsTotal: trackData.daily_payment || 0,
      monthTotal: trackData.month_payment || 0,
      outstandingPayment: trackData.outstanding_payment || 0,
      trackTotal: trackData.total_payment || 0,
    },
  };
};

/**
 * Build daily details array by merging daily_payments_json from both tracks
 * @param {Array} detDailyPayments - Deterministic track daily payments
 * @param {Array} probDailyPayments - Probabilistic track daily payments
 * @returns {Array} Merged daily details sorted by date
 */
const buildDailyDetails = (detDailyPayments, probDailyPayments) => {
  // Create lookup maps by date
  const detByDate = {};
  const probByDate = {};

  if (detDailyPayments) {
    detDailyPayments.forEach((day) => {
      detByDate[day.target_day] = day;
    });
  }

  if (probDailyPayments) {
    probDailyPayments.forEach((day) => {
      probByDate[day.target_day] = day;
    });
  }

  // Get all unique dates
  const allDates = [...new Set([
    ...Object.keys(detByDate),
    ...Object.keys(probByDate),
  ])].sort();

  // Build merged daily details
  return allDates.map((date) => {
    const detDay = detByDate[date];
    const probDay = probByDate[date];

    return {
      date,
      deterministic: detDay ? {
        rank: detDay.rank,
        dailyReward: detDay.payment || 0,
      } : null,
      probabilistic: probDay ? {
        rank: probDay.rank,
        dailyReward: probDay.payment || 0,
      } : null,
    };
  });
};

export default transformPaymentData;
